import { Box, Flex, type BoxProps, type FlexProps, type IconProps } from "@chakra-ui/react";
import classNames from "classnames";
import {
  Children,
  cloneElement,
  ComponentType,
  createContext,
  forwardRef,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { preventEventDefault, stopEventPropagation } from "helpers/browserEvent";
import { toPxString } from "helpers/styles";
import { uuidv4 } from "helpers/uuid";
import { useMergeRefs } from "hooks/useMergeRefs";
import CheckIcon from "vectors/Check";

type MenuRef =
  | ({
      focusIndex: (params: { index: number }) => void;
    } & HTMLElement)
  | null;

type MenuProps = {
  value?: string;
  onChange?: ChangeHandler;
  focus?: boolean;
  /** Enables/disables keybindings like "arrow up/down". Useful if you want to make nested menus and want to not override their keys. */
  keyboardActive?: boolean;
  /** Determines if the check mark should be shown next to the selected menu item */
  showSelected?: boolean;
  /** If you don't like the check mark, you can pass something else in. */
  selectionIcon?: ComponentType<{ selected: boolean }>;
  /** Determines if menu items with only string children will be parsed to extract their emoji. */
  showIcon?: boolean;
  children: ReactNode;
} & Omit<FlexProps, "onChange">;

export const Menu = forwardRef<MenuRef, MenuProps>(
  (
    {
      value: defaultValue,
      onChange,
      focus: shouldFocusFirstMenuItem = false,
      keyboardActive: isKeyboardActive = true,
      showSelected = false,
      selectionIcon = Check,
      showIcon = true,
      children,
      ...props
    },
    ref,
  ) => {
    const [value, onSelect] = useValue(defaultValue, onChange);

    const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);
    const mergedRef = useMergeRefs(setMenuElement, ref);

    const { focusElement, onFocus, menuItemClass } = useFocus(
      menuElement,
      shouldFocusFirstMenuItem,
    );

    useEffect(() => {
      if (menuElement == null) {
        return;
      }

      menuElement.focus = () => {
        onFocus({ index: 0 });
      };
    }, [menuElement, onFocus]);

    useKeyboard(onFocus, menuItemClass, isKeyboardActive);

    const optionsContext = useMemo(() => {
      return { showSelected, SelectionIcon: selectionIcon, showIcon };
    }, [selectionIcon, showIcon, showSelected]);

    const selectionContext = useMemo(() => {
      return { value, onSelect };
    }, [onSelect, value]);

    const focusContext = useMemo(() => {
      return { menuItemClass, focusElement, onFocus };
    }, [focusElement, menuItemClass, onFocus]);

    useImperativeHandle<MenuRef, MenuRef>(ref, () => {
      if (menuElement == null) {
        return null;
      }
      return Object.assign(menuElement, {
        focusIndex: (params: { index: number }) => {
          onFocus(params);
        },
      });
    }, [menuElement, onFocus]);

    return (
      <OptionsContext.Provider value={optionsContext}>
        <SelectionContext.Provider value={selectionContext}>
          <FocusContext.Provider value={focusContext}>
            <Flex
              className="menu"
              role="menu"
              data-testid="select-menu"
              ref={mergedRef}
              flexDirection="column"
              flex="1"
              maxWidth="full"
              fontSize="xs"
              {...props}
            >
              {children}
            </Flex>
          </FocusContext.Provider>
        </SelectionContext.Provider>
      </OptionsContext.Provider>
    );
  },
);

const MENU_ITEM_PADDING_X_PX = 8;
/** MenuItem is an individual choice in the menu. */
export const MenuItem = memo(
  ({
    value,
    icon,
    children,
    isDisabled,
    showSelected: showSelectedMenuItem,
    onSelect: onSelectMenuItem,
    ...props
  }: {
    /** This string will be passed into your onChange in the Menu. */
    value?: string;
    icon?: Icon;
    isDisabled?: boolean;
    children: ReactNode;
    /** Whether the menu item should be focused. If not provided, will fall back to the global Menu context setting. */
    showSelected?: boolean;
    onSelect?: (ev: ChangeEvent) => void;
  } & Omit<FlexProps, "onSelect">) => {
    const { value: selectedValue, onSelect: onDefaultSelect } = useContext(SelectionContext);
    const onSelect = useCallback(() => {
      if (isDisabled) {
        return;
      }

      if (value != null) {
        const event = new ChangeEvent(value);
        onDefaultSelect(event);
        onSelectMenuItem?.(event);
      }
    }, [isDisabled, onDefaultSelect, value, onSelectMenuItem]);

    const [element, setElement] = useState<HTMLElement | null>(null);
    useEffect(() => {
      if (element == null) {
        return () => {};
      }

      element.addEventListener("select", onSelect);
      return () => element.removeEventListener("select", onSelect);
    }, [element, onSelect]);

    const { showSelected: showSelectedMenuContext, SelectionIcon } = useContext(OptionsContext);
    children = normalizeChildren(children, icon);

    const showSelected = showSelectedMenuItem ?? showSelectedMenuContext;

    const { menuItemClass, focusElement, onFocus } = useContext(FocusContext);

    return (
      <Flex
        data-testid="select-menu-list-item"
        ref={setElement}
        className={classNames("menu-item", menuItemClass)}
        role="menuitem"
        data-group
        data-selected={value === selectedValue ? "" : undefined}
        data-focus={element === focusElement ? "" : undefined}
        data-value={value}
        tabIndex={0}
        _focus={{ backgroundColor: "surface.hover", color: "text.primary" }}
        cursor="pointer"
        minHeight="28px"
        paddingX={toPxString(MENU_ITEM_PADDING_X_PX)}
        paddingY="2px"
        fontWeight="medium"
        borderRadius="md"
        onClick={onSelect}
        onMouseEnter={() => {
          if (isDisabled) {
            return;
          }
          onFocus({ element });
        }}
        onMouseLeave={() => {
          if (isDisabled) {
            return;
          }
          onFocus({ element: null });
        }}
        onFocus={() => {
          element?.blur();
          if (isDisabled) {
            return;
          }
          onFocus({ element });
        }}
        onBlur={stopEventPropagation}
        alignItems="center"
        gap="1"
        opacity={isDisabled ? 0.5 : 1}
        overflowX="hidden"
        width="full"
        {...props}
      >
        {showSelected && (
          <Flex width="4" height="4" alignItems="center" justifyContent="center" flexShrink="0">
            <SelectionIcon selected={value === selectedValue} />
          </Flex>
        )}
        {children}
      </Flex>
    );
  },
);

export const MenuContent = memo(
  ({ value, icon }: { value: string | React.ReactElement; icon?: Icon }) => {
    const { showIcon } = useContext(OptionsContext);

    return (
      <Flex
        alignItems="center"
        fontWeight="medium"
        color="text.primary"
        gap="2"
        _groupFocus={{ color: "text.primary" }}
        width="full"
      >
        {showIcon && icon != null && cloneElement(icon)}
        {value}
      </Flex>
    );
  },
);

function useValue(defaultValue?: string, onChange?: ChangeHandler): [string, ChangeHandler] {
  const [value, setValue] = useState(defaultValue ?? "");
  useEffect(() => {
    setValue(defaultValue ?? "");
  }, [defaultValue]);

  const onSelect = useCallback(
    (event: ChangeEvent) => {
      if (onChange != null) {
        onChange(event);
      }
      if (!event.defaultPrevented) {
        setValue(event.value);
      }
    },
    [onChange],
  );
  return [value, onSelect];
}

function useFocus(
  menuElement: HTMLElement | null,
  shouldFocusFirstMenuItem: boolean,
): {
  focusElement: HTMLElement | null;
  onFocus: ({
    offset,
    index,
    element,
  }: {
    offset?: number;
    index?: number | null;
    element?: HTMLElement | null;
  }) => void;
  menuItemClass: string;
} {
  const [_, setFocusIndex] = useState<number | null>(null);

  const menuItemClass = useMemo(() => `menu-item-${uuidv4()}`, []);
  const [focusElement, setFocusElement] = useState<HTMLElement | null>(null);
  const onFocus = useCallback(
    ({
      offset,
      index,
      element,
    }: {
      offset?: number;
      index?: number | null;
      element?: HTMLElement | null;
    }) => {
      if (menuElement == null || (offset == null && index == null && element == null)) {
        return;
      }

      const menuItems = [
        ...menuElement.querySelectorAll<HTMLElement>(getMenuItemSelector(menuItemClass)),
      ];
      const selectedItem = menuElement.querySelector(
        `${getMenuItemSelector(menuItemClass)}[data-focus]`,
      );
      setFocusIndex((currentIndex) => {
        let newIndex: number | null = null;
        if (offset != null) {
          if (currentIndex == null) {
            return currentIndex;
          } else if (currentIndex < menuItems.length && selectedItem != null) {
            newIndex = (currentIndex + offset) % menuItems.length;
            if (currentIndex + offset < 0) {
              newIndex = menuItems.length - 1;
            }
          } else {
            newIndex = 0;
          }
        } else if (index != null && index < menuItems.length) {
          newIndex = index;
        } else if (element != null) {
          newIndex = menuItems.findIndex((menuItem) => menuItem === element);
          if (newIndex === -1) {
            return currentIndex;
          }
        }

        if (newIndex != null && newIndex >= menuItems.length) {
          newIndex = 0;
        }

        const menuItem = newIndex != null ? menuItems[newIndex] : null;
        setFocusElement(menuItem);
        if (menuItem != null) {
          scrollToElement(menuItem, menuElement, menuItems.length - 1, newIndex, currentIndex);
        }

        return newIndex;
      });
    },
    [menuElement, menuItemClass],
  );

  useEffect(() => {
    if (shouldFocusFirstMenuItem) {
      onFocus({ index: 0 });
    }
  }, [onFocus, shouldFocusFirstMenuItem]);

  return { focusElement, onFocus, menuItemClass };
}

function getMenuItemSelector(menuItemClass: string) {
  return `.menu-item.${menuItemClass}`;
}

function scrollToElement(
  element: HTMLElement,
  parent: HTMLElement,
  lastItemIndex: number,
  newIndex: number | null,
  currentIndex: number | null,
) {
  if (newIndex === 0 && currentIndex === lastItemIndex) {
    parent.scroll({ behavior: "instant", top: 0 });
    return;
  } else if (newIndex === lastItemIndex) {
    parent.scroll({
      behavior: currentIndex === 0 ? "instant" : "smooth",
      top: parent.scrollHeight,
    });
    return;
  }

  const containerRect = parent.getBoundingClientRect();
  const targetRect = element.getBoundingClientRect();
  const isFullyVisible =
    targetRect.top >= containerRect.top && targetRect.bottom <= containerRect.bottom;

  if (!isFullyVisible) {
    element.scrollIntoView({
      behavior: "smooth",
      block: targetRect.top >= containerRect.top ? "start" : "end",
    });
  }
}

function useKeyboard(
  onFocus: ({ offset, element }: { offset?: number; element?: HTMLElement | null }) => void,
  menuItemClass: string,
  isActive: boolean,
) {
  useEffect(() => {
    if (!isActive) {
      return () => {};
    }

    const callback = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Enter") {
        const selectedElement = document.querySelector(
          `${getMenuItemSelector(menuItemClass)}[data-focus]`,
        );
        selectedElement?.dispatchEvent(new Event("select"));
      } else if (event.key === "ArrowDown") {
        preventEventDefault(event);
        onFocus({ offset: 1 });
      } else if (event.key === "ArrowUp") {
        preventEventDefault(event);
        onFocus({ offset: -1 });
      } else if (
        event.key === "Tab" &&
        document.activeElement?.classList?.contains(menuItemClass)
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", callback);
    return () => document.removeEventListener("keydown", callback);
  }, [isActive, menuItemClass, onFocus]);
}

function normalizeChildren(children: ReactNode, icon: Icon | undefined) {
  return Children.toArray(children).map((child, i) => {
    if (typeof child === "string") {
      return <MenuContent key={i} value={child} icon={icon} />;
    }
    return child;
  });
}

const Check = ({ selected }: { selected: boolean }) => {
  if (!selected) {
    return null;
  }
  return <CheckIcon color="selection.500" boxSize="3" />;
};

const SelectionContext = createContext<{
  value: string;
  onSelect: ChangeHandler;
}>({
  value: "",
  onSelect: () => {},
});

const OptionsContext = createContext<{
  showSelected: boolean;
  SelectionIcon: ComponentType<{ selected: boolean }>;
  showIcon: boolean;
}>({ showSelected: true, SelectionIcon: Check, showIcon: false });

const FocusContext = createContext<{
  menuItemClass: string;
  focusElement: HTMLElement | null;
  onFocus: ({ offset, element }: { offset?: number; element?: HTMLElement | null }) => void;
}>({
  menuItemClass: uuidv4(),
  focusElement: null,
  onFocus: () => {},
});

type Icon = ReactElement<{ _groupFocus?: IconProps["_groupFocus"] }>;

export class ChangeEvent extends Event {
  value: string;

  constructor(value: string) {
    super("change", { cancelable: true });
    this.value = value;
  }
}

type ChangeHandler = ((value: ChangeEvent) => void) | ((value: ChangeEvent) => Promise<void>);
